import XDate from 'xdate'

const ONE_SECOND_MS = 1000
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS
const ONE_DAY_MS = 24 * ONE_HOUR_MS
const ONE_WEEK_MS = 7 * ONE_DAY_MS

interface FormatRelativeTimeOptions {
  capitalised?: boolean
  yesterdayLabel?: boolean
  emptyTimestampLabel?: string | null
}

/**
 * Formats a millis timestamp into a renter-friendly relative-time
 * string. Configurable so callers can tweak capitalisation, the
 * "Yesterday" branch, and the fallback when the timestamp is 0.
 *
 * @param {number} timestamp - the millis timestamp to format
 * @param {FormatRelativeTimeOptions} options - capitalisation / yesterday / empty-fallback overrides
 * @returns {string} the relative-time string
 */
const formatRelativeTime = (
  timestamp: number,
  options: FormatRelativeTimeOptions = {},
): string => {
  const capitalised = options.capitalised ?? true
  const yesterdayLabel = options.yesterdayLabel ?? true
  const emptyTimestampLabel = options.emptyTimestampLabel ?? null

  if (timestamp === 0 && emptyTimestampLabel !== null) {
    return emptyTimestampLabel
  }

  const now = Date.now()
  const elapsed = now - timestamp

  if (elapsed < ONE_MINUTE_MS) {
    return capitalised === true ? 'Just now' : 'just now'
  }

  if (elapsed < ONE_HOUR_MS) {
    const minutes = Math.floor(elapsed / ONE_MINUTE_MS)
    return `${minutes}m ago`
  }

  if (elapsed < ONE_DAY_MS) {
    const hours = Math.floor(elapsed / ONE_HOUR_MS)
    return `${hours}h ago`
  }

  if (elapsed < 2 * ONE_DAY_MS && yesterdayLabel === true) {
    return capitalised === true ? 'Yesterday' : 'yesterday'
  }

  if (elapsed < ONE_WEEK_MS) {
    const days = Math.floor(elapsed / ONE_DAY_MS)
    return `${days}d ago`
  }

  return new XDate(timestamp).toString('MMM d')
}

export {
  type FormatRelativeTimeOptions,
  ONE_SECOND_MS,
  ONE_MINUTE_MS,
  ONE_HOUR_MS,
  ONE_DAY_MS,
  ONE_WEEK_MS,
  formatRelativeTime,
}
